/**
 * Choosing what things are called, one noun at a time.
 *
 * Presets were the only way in, and a preset is an all-or-nothing swap. An agency that runs an
 * estate of houses *and* a parade of shops had to pick whichever preset was least wrong and live
 * with the rest — which is most agencies here, not an edge case.
 *
 * So each noun is chosen on its own, from the words people actually use, with "Something else"
 * for the ones nobody thought of. The presets stay as a starting point: they fill the boxes in
 * one click and everything remains editable afterwards.
 *
 * Singular and plural are set together. Letting them drift apart produces "estate / shops" the
 * first time somebody edits one box and not the other, and every screen inherits it.
 */

import { useState } from "react";
import {
  VOCABULARY_PRESETS,
  VOCABULARY_TERMS,
  defaultPlural,
  withTerm,
  type Vocabulary,
  type VocabularyTerm,
} from "@rentorng/core";
import { Button, Field, Input, Select } from "./ui";

const OTHER = "__other__";

export function VocabularyEditor({
  value,
  onChange,
  onSave,
  readOnly,
  saving,
}: {
  value: Vocabulary;
  onChange: (next: Vocabulary) => void;
  onSave: () => void;
  readOnly?: boolean;
  saving?: boolean;
}) {
  /** Which terms the user has switched to free text. A word off-list implies it on first render. */
  const [custom, setCustom] = useState<Record<string, boolean>>({});

  const isCustom = (term: VocabularyTerm) =>
    custom[term.key] ?? !term.options.some((option) => option.one === value[term.key]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-sm text-ink-500">Start from:</span>
        {VOCABULARY_PRESETS.map((preset) => (
          <Button
            key={preset.name}
            variant="secondary"
            size="sm"
            disabled={readOnly}
            title={preset.description}
            onClick={() => {
              setCustom({});
              onChange(preset.vocabulary);
            }}
          >
            {preset.name}
          </Button>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {VOCABULARY_TERMS.map((term) => {
          const singular = value[term.key];
          const plural = term.pluralKey ? value[term.pluralKey] : undefined;
          const usingCustom = isCustom(term);

          return (
            <Field key={term.key} label={term.label} htmlFor={`vocab-${term.key}`} hint={term.hint}>
              <Select
                id={`vocab-${term.key}`}
                value={usingCustom ? OTHER : singular}
                disabled={readOnly}
                onChange={(event) => {
                  const next = event.target.value;
                  if (next === OTHER) {
                    setCustom((current) => ({ ...current, [term.key]: true }));
                    return;
                  }
                  setCustom((current) => ({ ...current, [term.key]: false }));
                  const option = term.options.find((o) => o.one === next);
                  onChange(withTerm(value, term, next, option?.many));
                }}
              >
                {term.options.map((option) => (
                  <option key={option.one} value={option.one}>
                    {term.pluralKey ? `${option.one} / ${option.many ?? defaultPlural(option.one)}` : option.one}
                  </option>
                ))}
                <option value={OTHER}>Something else…</option>
              </Select>

              {usingCustom ? (
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <Input
                    aria-label={`${term.label}, singular`}
                    placeholder="one of them"
                    value={singular}
                    disabled={readOnly}
                    onChange={(event) => onChange(withTerm(value, term, event.target.value, plural))}
                  />
                  {term.pluralKey ? (
                    <Input
                      aria-label={`${term.label}, plural`}
                      placeholder="more than one"
                      value={plural ?? ""}
                      disabled={readOnly}
                      onChange={(event) =>
                        onChange({ ...value, [term.pluralKey as keyof Vocabulary]: event.target.value })
                      }
                    />
                  ) : null}
                </div>
              ) : null}
            </Field>
          );
        })}
      </div>

      {/* Says the choice back as a sentence. Twelve dropdowns are hard to check one by one; one
          sentence in the product's own voice is not. */}
      <p className="mt-4 rounded-[var(--radius-surface)] bg-ink-50 px-4 py-3 text-sm text-ink-600">
        You will see: <strong>{value.properties}</strong> made up of <strong>{value.units}</strong>, let to{" "}
        <strong>{value.tenants}</strong> on <strong>{value.leases}</strong>, with <strong>{value.rent}</strong> billed
        by <strong>{value.invoices}</strong> and remitted to <strong>{value.landlords}</strong>.
      </p>

      {!readOnly ? (
        <Button className="mt-4" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Save wording"}
        </Button>
      ) : null}
    </div>
  );
}
